import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import api from '../utils/api';
import { collapse, createQueue } from '../utils/offlineQueue';
import {
  IDLE, OFFLINE, RUNNING, backoffMs, runSync, shouldSync,
} from '../utils/syncEngine';
import { readOnlyNow, refuseWhileViewing } from '../utils/viewAs';

/*
 * When a sync is attempted, and what the rest of the app can see about it.
 *
 * Four triggers, and each exists because the others miss a case:
 *
 *   - `online`: the obvious one, and the least reliable. See syncEngine.js.
 *   - becoming visible again: a phone in a pocket fires no events; the tab
 *     coming back is often the first moment anything is true again.
 *   - a poll: a device that never lost its link but whose SERVER was down
 *     gets no event at all when the server returns.
 *   - enqueue: something was just done. If there is a network, use it.
 *
 * All four go through shouldSync(), so the rules live in one place and a
 * flapping connection cannot turn four triggers into four times the requests.
 */

const SyncContext = createContext(null);
const POLL_MS = 60 * 1000;

export function SyncProvider({ children, queue: injectedQueue, storage = window.localStorage }) {
  const queue = useMemo(() => injectedQueue || createQueue(), [injectedQueue]);
  const [state, setState] = useState(IDLE);
  // The entries themselves, not just how many: a screen showing what it queued
  // (see pages/Collections.js) needs the records to render them, and a count
  // would only tell it that something somewhere is waiting.
  const [pendingEntries, setPendingEntries] = useState([]);
  const [pending, setPending] = useState(0);
  const [conflicts, setConflicts] = useState([]);
  const [lastError, setLastError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  // Refs, not state: a trigger firing must read the CURRENT values, and a
  // stale closure would let two runs start at once.
  const running = useRef(false);
  const attempt = useRef(0);
  const nextAttemptAt = useRef(0);

  const refresh = useCallback(async () => {
    const entries = await queue.pending();
    setPendingEntries(entries);
    setPending(entries.length);
    setConflicts(await queue.conflicts());
  }, [queue]);

  useEffect(() => { refresh(); }, [refresh]);

  const sync = useCallback(async ({ force = false } = {}) => {
    // Viewing the app as another role: the queue holds the admin's own
    // writes, and they wait for Exit (utils/viewAs.js).
    // An executive's account (0098) may only read: it sends nothing either.
    if (readOnlyNow(storage)) return { go: false, why: 'viewingAs' };
    const online = typeof navigator === 'undefined' ? true : navigator.onLine;
    const pendingCount = await queue.count();
    const verdict = shouldSync({
      online, running: running.current, pendingCount,
      nextAttemptAt: force ? 0 : nextAttemptAt.current,
    });
    if (!verdict.go) {
      if (verdict.why === 'offline') setState(OFFLINE);
      return verdict;
    }

    running.current = true;
    setState(RUNNING);
    try {
      const result = await runSync({ api, queue, collapse, storage });
      setLastResult(result);
      if (result.ok) {
        attempt.current = 0;
        nextAttemptAt.current = 0;
        setState(IDLE);
        setLastError(null);
      } else {
        attempt.current += 1;
        nextAttemptAt.current = Date.now() + backoffMs(attempt.current);
        setState(result.state);
        setLastError(result.error);
      }
      await refresh();
      // The server had more than it would send in one answer. Come straight
      // back for the rest rather than waiting for the next trigger.
      if (result.ok && result.more) setTimeout(() => sync({ force: true }), 0);
      return result;
    } finally {
      running.current = false;
    }
  }, [queue, storage, refresh]);

  /** Record an action and try to send it. Offline, it just waits. */
  const enqueue = useCallback(async (table, record) => {
    // Read-only while viewing as a role: a write queued now would be sent
    // after Exit, as the admin.
    refuseWhileViewing(storage);
    const entry = await queue.enqueue(table, record);
    await refresh();
    sync();
    return entry;
  }, [queue, refresh, sync, storage]);

  const dismissConflict = useCallback(async (id) => {
    await queue.dismissConflict(id);
    await refresh();
  }, [queue, refresh]);

  useEffect(() => {
    const onOnline = () => { attempt.current = 0; nextAttemptAt.current = 0; sync(); };
    const onOffline = () => setState(OFFLINE);
    const onVisible = () => { if (document.visibilityState === 'visible') sync(); };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(() => sync(), POLL_MS);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [sync]);

  const value = useMemo(() => ({
    state, pending, pendingEntries, conflicts, lastError, lastResult,
    sync, enqueue, dismissConflict, refresh,
  }), [state, pending, pendingEntries, conflicts, lastError, lastResult,
       sync, enqueue, dismissConflict, refresh]);

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

export function useSync() {
  const context = useContext(SyncContext);
  if (!context) throw new Error('useSync must be used inside a SyncProvider');
  return context;
}

export default SyncContext;
